import joi from 'joi';
import { useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import { IDataRequest, IResearchProposal } from '@vivli/features/data-requests/infrastructure/interface';
import { fundingFormSchema } from './use-funding-form.hook';
import { getResearchProposalSchema } from './use-research-proposal-form.hook';
import { statisticalAnalysisFormSchema } from './use-statistical-analysis-form.hook';
import { researchTeamFormSchema } from './use-research-team-form.hook';
import { DataRequestStatusEnum } from '@vivli/features/data-requests/infrastructure/enum';
import { JoiValidationOptionsConstant } from '@vivli/shared/infrastructure/constants';
import { publishingFormSchema } from './use-publish-approved-form.hook';

const getFormsSchema = (researchProposal: IResearchProposal, isAdmin: boolean, requestStartDate: Date) => {
    return joi
        .object({
            requestTitle: joi.string().required().label('Request Title'),
            status: joi.string().allow(DataRequestStatusEnum.Draft),
            otherInformation: joi.optional().label('Other Information'),
            researchTeam: researchTeamFormSchema,
            researchProposal: getResearchProposalSchema(isAdmin, researchProposal, requestStartDate),
            funding: fundingFormSchema,
            statisticalAnalysisPlan: statisticalAnalysisFormSchema,
            approvedRequestsPublishing: publishingFormSchema,
            requirementsAndAttestations: joi.object({
                hasCertifiedCompleteAndAccurate: joi
                    .boolean()
                    .label('Certification Acknowledgment')
                    .valid(true)
                    .messages({ 'any.only': '{{#label}} must be checked.' })
            })
        })
        .options(JoiValidationOptionsConstant);
};

export function useDataRequestForm(dataRequest: IDataRequest, isAdmin: boolean, mode: 'onBlur' | 'onChange' | 'onTouched' | 'all' = 'all') {
    const schema = getFormsSchema(dataRequest?.researchProposal, isAdmin, dataRequest?.createdDate);

    // handle null values, undefined is ok, null is not
    const updatedDataRequest = !dataRequest
        ? {}
        : {
              ...dataRequest,
              researchTeam: {
                  ...dataRequest?.researchTeam,
                  researchTeamDetails: dataRequest.researchTeam?.researchTeamDetails?.map((rtd) => ({
                      ...rtd,
                      country: rtd.country || undefined,
                      yearReceived: rtd.yearReceived || undefined
                  }))
              }
          };

    return useForm<IDataRequest>({
        mode,
        resolver: joiResolver(schema.options(JoiValidationOptionsConstant)),
        defaultValues: updatedDataRequest,
        reValidateMode: 'onChange'
    });
}
